import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { InboxFile } from '../types';
import type { InboxOAuthMessage } from '../utils/inboxCallback';
import { INBOX_OAUTH_MESSAGE_TYPE } from '../utils/inboxCallback';
import { PasswordInput, Button } from './ui';
import { classifyError } from '../utils/errors';
import {
  IconAlert,
  IconGoogle,
  IconMicrosoft,
  IconZoho,
  IconFile,
  IconCheckCircle,
} from './icons';

interface InboxModeProps {
  apiKey: string;
  apiBaseUrl?: string;
  inboxConfig?: {
    redirectUri: string; // Required for OAuth
    casTypes?: ('cdsl' | 'nsdl' | 'cams' | 'kfintech')[];
    startDate?: string;
    endDate?: string;
  };
  prefillPan?: string; // Prefilled PAN for password
  onFileSelect: (file: InboxFile, password: string) => void;
  onEvent?: (event: string, metadata?: any) => void;
  /**
   * Optional escape hatches surfaced on the empty-inbox state. When
   * Gmail returns no statements, we offer the user the most likely
   * next steps instead of leaving them at a dead end. Wired by the
   * parent only when the corresponding feature is enabled.
   */
  onJumpToRequestCas?: () => void;
  onJumpToInbound?: () => void;
}

type InboxState = 'CONNECT' | 'LOADING' | 'FILES' | 'PASSWORD' | 'ERROR';

type InboxProvider = 'gmail' | 'outlook' | 'zoho';

// Per-provider display metadata. Brand colours are intentionally NOT
// themed — provider trade-dress must stay recognisable to the end user.
const PROVIDERS: Record<InboxProvider, {
  label: string;
  icon: React.ReactNode;
  brandColor: string;
  signInLabel: string;
  // User-facing name used in error / connecting copy.
  name: string;
  // URL where the user can revoke OAuth access after connecting.
  permissionsUrl: string;
}> = {
  gmail: {
    label: 'Continue with Google',
    icon: <IconGoogle />,
    brandColor: '#4285F4',
    signInLabel: 'Opening Google sign-in…',
    name: 'Google',
    permissionsUrl: 'https://myaccount.google.com/permissions',
  },
  outlook: {
    label: 'Continue with Microsoft',
    icon: <IconMicrosoft />,
    brandColor: '#0072C6',
    signInLabel: 'Opening Microsoft sign-in…',
    name: 'Microsoft',
    permissionsUrl: 'https://account.live.com/consent/Manage',
  },
  zoho: {
    label: 'Continue with Zoho',
    icon: <IconZoho />,
    brandColor: '#E43227',
    signInLabel: 'Opening Zoho sign-in…',
    name: 'Zoho',
    permissionsUrl: 'https://accounts.zoho.com/oauth2/revoke',
  },
};

// Google brand blue — intentionally NOT themed (provider trade-dress).
// Used for the connecting spinner highlight; the IconGoogle SVG itself
// already carries the four brand colours internally.
const GOOGLE_BLUE = '#4285F4';

export const InboxMode: React.FC<InboxModeProps> = ({
  apiKey,
  apiBaseUrl = 'https://api.casparser.in',
  inboxConfig,
  prefillPan,
  onFileSelect,
  onEvent,
  onJumpToRequestCas,
  onJumpToInbound,
}) => {
  const [state, setState] = useState<InboxState>('CONNECT');
  const [inboxToken, setInboxToken] = useState<string | null>(null);
  const [connectedEmail, setConnectedEmail] = useState<string | null>(null);
  const [files, setFiles] = useState<InboxFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<InboxFile | null>(null);
  const [password, setPassword] = useState(prefillPan || '');
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<InboxProvider>('gmail');
  const isConnectingRef = useRef(false);
  const popupRef = useRef<Window | null>(null);
  const popupCheckInterval = useRef<number | null>(null);
  const hasLoadedFilesRef = useRef(false);

  const emitEvent = useCallback((event: string, metadata?: any) => {
    onEvent?.(event, { timestamp: Date.now(), ...metadata });
  }, [onEvent]);

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Validate message type
      const data = event.data as InboxOAuthMessage;
      if (data?.type !== INBOX_OAUTH_MESSAGE_TYPE) return;

      // Clear popup tracking
      if (popupCheckInterval.current) {
        clearInterval(popupCheckInterval.current);
        popupCheckInterval.current = null;
      }
      popupRef.current = null;
      setIsConnecting(false);
      isConnectingRef.current = false;

      if (data.error) {
        const providerName = PROVIDERS[selectedProvider].name;
        setError(
          data.error === 'access_denied'
            ? `You declined to give ${providerName} access. No problem — you can try a different method.`
            : `${providerName} sign-in didn\'t complete. Please try again.`,
        );
        setState('ERROR');
        emitEvent('INBOX_ERROR', { error: data.error });
        return;
      }

      if (data.success && data.inbox_token && data.email && !hasLoadedFilesRef.current) {
        hasLoadedFilesRef.current = true;
        setInboxToken(data.inbox_token);
        setConnectedEmail(data.email);
        // Emit token to developers for future access
        emitEvent('INBOX_CONNECTED', { 
          email: data.email, 
          inbox_token: data.inbox_token // Share token for developer storage
        });
        loadFiles(data.inbox_token);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for stored token from sessionStorage (fallback for same-page redirect)
  const checkSessionStorage = useCallback(() => {
    const storedToken = sessionStorage.getItem('cas_connect_inbox_token');
    const storedEmail = sessionStorage.getItem('cas_connect_inbox_email');
    const storedError = sessionStorage.getItem('cas_connect_inbox_error');

    if (storedError) {
      sessionStorage.removeItem('cas_connect_inbox_error');
      const providerName = PROVIDERS[selectedProvider].name;
      setError(
        storedError === 'access_denied'
          ? `You declined to give ${providerName} access. No problem — you can try a different method.`
          : `${providerName} sign-in didn\'t complete. Please try again.`,
      );
      setState('ERROR');
      setIsConnecting(false);
      isConnectingRef.current = false;
      emitEvent('INBOX_ERROR', { error: storedError });
      return true;
    }

    if (storedToken && storedEmail && !hasLoadedFilesRef.current) {
      hasLoadedFilesRef.current = true;
      sessionStorage.removeItem('cas_connect_inbox_token');
      sessionStorage.removeItem('cas_connect_inbox_email');
      setInboxToken(storedToken);
      setConnectedEmail(storedEmail);
      setIsConnecting(false);
      isConnectingRef.current = false;
      emitEvent('INBOX_CONNECTED', { 
        email: storedEmail,
        inbox_token: storedToken
      });
      loadFiles(storedToken);
      return true;
    }
    return false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check sessionStorage on mount
  useEffect(() => {
    checkSessionStorage();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iOS Chrome fallback: when popup opens as a new tab, postMessage won't work.
  // Listen for visibilitychange to recover the token from sessionStorage when
  // the user switches back to the original tab.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isConnectingRef.current) {
        checkSessionStorage();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupCheckInterval.current) {
        clearInterval(popupCheckInterval.current);
      }
    };
  }, []);

  const loadFiles = async (token: string) => {
    setState('LOADING');
    try {
      const body: any = {};
      if (inboxConfig?.casTypes) body.cas_types = inboxConfig.casTypes;
      if (inboxConfig?.startDate) body.start_date = inboxConfig.startDate;
      if (inboxConfig?.endDate) body.end_date = inboxConfig.endDate;

      const resp = await fetch(`${apiBaseUrl}/v4/inbox/cas`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
          'x-inbox-token': token,
        },
        body: JSON.stringify(body),
      });

      if (!resp.ok) {
        let errorData: any = {};
        try {
          errorData = await resp.json();
        } catch {
          /* ignore — we'll show the generic message */
        }
        // Throw the same `{ response: { status, data }, status,
        // message }` envelope the api.ts client uses so classifyError
        // can branch on HTTP status (401 → AUTHENTICATION,
        // 429 → RATE_LIMITED, 5xx → NETWORK). A plain `throw new
        // Error()` would lose the status and fall back to the
        // generic INBOX_ERROR code.
        throw {
          response: { status: resp.status, data: errorData },
          status: resp.status,
          message:
            errorData?.msg ||
            'We couldn\'t load statements from your inbox. Please try again.',
        };
      }

      const data = await resp.json();
      setFiles(data.files || []);
      setState('FILES');
      emitEvent('INBOX_FILES_LOADED', { count: data.files?.length || 0 });
    } catch (err: any) {
      // Run through classifyError so HTTP-status-derived codes
      // (AUTHENTICATION / NETWORK / RATE_LIMITED) flow into analytics
      // even though the user-facing message stays the same. The
      // generic INBOX_ERROR fallback covers everything else.
      const classified = classifyError(err, 'INBOX_ERROR');
      setError(
        classified.message ||
          'We couldn\'t load statements from your inbox. Please try again.',
      );
      setState('ERROR');
      emitEvent('INBOX_ERROR', {
        error: classified.message,
        code: classified.code,
      });
    }
  };

  const handleConnect = async (provider: InboxProvider = 'gmail') => {
    // Validate redirectUri is configured
    if (!inboxConfig?.redirectUri) {
      setError(
        'Email import isn\'t set up for this app. Please use a different method, such as uploading your statement file directly.',
      );
      setState('ERROR');
      emitEvent('INBOX_ERROR', { error: 'Missing redirectUri configuration' });
      return;
    }

    setSelectedProvider(provider);
    emitEvent('INBOX_CONNECT_STARTED', { provider });
    setIsConnecting(true);
    isConnectingRef.current = true;

    const state = generateOAuthState();
    sessionStorage.setItem('inbox_oauth_state', state);

    // Get OAuth URL from API
    let oauthUrl: string;
    try {
      const response = await fetch(`${apiBaseUrl}/v4/inbox/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({
          redirect_uri: inboxConfig.redirectUri,
          state,
          provider,
        }),
      });
      let data: any = {};
      try {
        data = await response.json();
      } catch {
        /* ignore — we'll throw a status-bearing envelope below */
      }
      // Check response.ok first so 401/429/5xx flow through
      // classifyError with their HTTP-status semantics. A plain
      // `data.status !== 'success'` check would miss those entirely.
      if (!response.ok || data.status !== 'success' || !data.oauth_url) {
        throw {
          response: { status: response.status, data },
          status: response.status,
          message:
            data?.msg || `Couldn\'t start ${PROVIDERS[provider].name} sign-in.`,
        };
      }
      oauthUrl = data.oauth_url;
    } catch (err: any) {
      const classified = classifyError(err, 'INBOX_ERROR');
      const providerName = PROVIDERS[provider].name;
      setError(
        `We couldn\'t start ${providerName} sign-in. Please try again, or use a different method.`,
      );
      setState('ERROR');
      setIsConnecting(false);
      isConnectingRef.current = false;
      emitEvent('INBOX_ERROR', {
        error: classified.message,
        code: classified.code,
      });
      return;
    }
    
    // Open OAuth in popup window
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;
    
    popupRef.current = window.open(
      oauthUrl,
      'PortfolioConnectOAuth',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
    );

    // Check if popup was blocked. Most browsers return null or a
    // pre-closed window object in this case. We surface a user-friendly
    // message and offer to fall back to forwarding their email — the
    // alternative path doesn't require popups at all.
    if (!popupRef.current || popupRef.current.closed) {
      const providerName = PROVIDERS[provider].name;
      setError(
        `Your browser blocked the ${providerName} sign-in window. You can allow pop-ups for this site and try again, or import by forwarding your statement email instead.`,
      );
      setState('ERROR');
      setIsConnecting(false);
      isConnectingRef.current = false;
      emitEvent('INBOX_ERROR', { error: 'Popup blocked' });
      return;
    }

    // Focus the popup
    popupRef.current.focus();

    // Monitor popup for closure (user cancelled)
    popupCheckInterval.current = window.setInterval(() => {
      if (popupRef.current?.closed) {
        if (popupCheckInterval.current) {
          clearInterval(popupCheckInterval.current);
          popupCheckInterval.current = null;
        }
        popupRef.current = null;
        // Only reset if we're still connecting (didn't receive success message)
        if (isConnectingRef.current) {
          setIsConnecting(false);
          isConnectingRef.current = false;
          // Don't show error - user may have just closed the popup
        }
      }
    }, 500);
  };

  const handleDisconnect = async () => {
    if (!inboxToken) return;
    
    try {
      await fetch(`${apiBaseUrl}/v4/inbox/disconnect`, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
          'x-inbox-token': inboxToken,
        },
      });
    } catch (e) {
      // Ignore disconnect errors
    }
    
    setInboxToken(null);
    setConnectedEmail(null);
    setFiles([]);
    setSelectedFile(null);
    setPassword(prefillPan || '');
    setSelectedProvider('gmail');
    hasLoadedFilesRef.current = false;
    setState('CONNECT');
    emitEvent('INBOX_DISCONNECTED');
  };

  const handleFileClick = (file: InboxFile) => {
    setSelectedFile(file);
    emitEvent('INBOX_FILE_SELECTED', { filename: file.filename, cas_type: file.cas_type });
  };

  const handleImport = () => {
    if (selectedFile) {
      // Go to password step instead of immediately calling onFileSelect
      setState('PASSWORD');
    }
  };

  const handlePasswordSubmit = () => {
    if (selectedFile) {
      onFileSelect(selectedFile, password);
    }
  };

  // ~96 bits of entropy, base64url-encoded. Used as the OAuth `state`
  // parameter — must be unguessable by an attacker who can lure the
  // user back to the redirect URI. `Math.random()` is not cryptographic
  // and the previous `.toString(36).substring(7)` only yielded ~31-36
  // bits, well below RFC 6749 §10.12 recommendations.
  function generateOAuthState(): string {
    const cryptoObj: Crypto | undefined =
      typeof window !== 'undefined' && window.crypto
        ? window.crypto
        : undefined;
    if (cryptoObj?.getRandomValues) {
      const bytes = new Uint8Array(12);
      cryptoObj.getRandomValues(bytes);
      // base64url without padding — safe in URLs without further encoding
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      return btoa(bin)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    }
    // Fallback: combine two Math.random() draws + timestamp. Not
    // cryptographic, but better than 6 base-36 chars. We reach this
    // only on environments without `window.crypto`, which in 2026 are
    // effectively absent in browsers.
    return (
      Math.random().toString(36).slice(2) +
      Math.random().toString(36).slice(2) +
      Date.now().toString(36)
    );
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const getCasTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cdsl: 'CDSL',
      nsdl: 'NSDL',
      // For end users, the underlying mutual fund registrar (CAMS or
      // KFintech) is implementation detail — both surface as "Mutual Funds".
      cams: 'Mutual Funds',
      kfintech: 'Mutual Funds',
    };
    return labels[type] || type.toUpperCase();
  };

  if (state === 'CONNECT') {
    const activeProvider = PROVIDERS[selectedProvider];
    return (
      <div>

        {/* Primary Gmail button */}
        <button
          onClick={() => handleConnect('gmail')}
          disabled={isConnecting}
          style={{
            width: '100%',
            padding: '12px 18px',
            fontSize: 15,
            fontWeight: 600,
            backgroundColor: isConnecting
              ? 'var(--pc-color-surface-subtle)'
              : 'var(--pc-color-surface)',
            color: isConnecting
              ? 'var(--pc-color-text-faded)'
              : 'var(--pc-color-text)',
            border: '1.5px solid var(--pc-color-border)',
            borderRadius: 'var(--pc-radius-md)',
            cursor: isConnecting ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            transition: 'border-color 0.15s, background-color 0.15s',
            fontFamily: 'inherit',
          }}
          onMouseOver={(e) => {
            if (!isConnecting) {
              e.currentTarget.style.borderColor = GOOGLE_BLUE;
              e.currentTarget.style.backgroundColor =
                'var(--pc-color-surface-subtle)';
            }
          }}
          onMouseOut={(e) => {
            if (!isConnecting) {
              e.currentTarget.style.borderColor = 'var(--pc-color-border)';
              e.currentTarget.style.backgroundColor = 'var(--pc-color-surface)';
            }
          }}
        >
          {isConnecting ? (
            <>
              <div
                style={{
                  width: 16,
                  height: 16,
                  border: '2px solid var(--pc-color-border-strong)',
                  borderTopColor: activeProvider.brandColor,
                  borderRadius: '50%',
                  animation: 'pc-spin 1s linear infinite',
                }}
                aria-hidden="true"
              />
              <span>{activeProvider.signInLabel}</span>
            </>
          ) : (
            <>
              <IconGoogle />
              <span>Continue with Google</span>
            </>
          )}
        </button>

        {/* Secondary providers: Microsoft + Zoho */}
        {!isConnecting && (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                margin: '16px 0',
              }}
            >
              <div
                style={{
                  flex: 1,
                  height: 1,
                  backgroundColor: 'var(--pc-color-border)',
                }}
              />
              <span
                style={{
                  fontSize: 11,
                  color: 'var(--pc-color-text-faded)',
                  fontWeight: 500,
                }}
              >
                or use a different provider
              </span>
              <div
                style={{
                  flex: 1,
                  height: 1,
                  backgroundColor: 'var(--pc-color-border)',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              {(['outlook', 'zoho'] as InboxProvider[]).map((p) => {
                const meta = PROVIDERS[p];
                return (
                  <button
                    key={p}
                    onClick={() => handleConnect(p)}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      fontSize: 13,
                      fontWeight: 600,
                      backgroundColor: 'var(--pc-color-surface)',
                      color: 'var(--pc-color-text)',
                      border: '1.5px solid var(--pc-color-border)',
                      borderRadius: 'var(--pc-radius-md)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'border-color 0.15s, background-color 0.15s',
                      fontFamily: 'inherit',
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.borderColor = meta.brandColor;
                      e.currentTarget.style.backgroundColor =
                        'var(--pc-color-surface-subtle)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.borderColor =
                        'var(--pc-color-border)';
                      e.currentTarget.style.backgroundColor =
                        'var(--pc-color-surface)';
                    }}
                  >
                    {meta.icon}
                    <span>{meta.name}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <p
          style={{
            fontSize: 13,
            color: 'var(--pc-color-text-muted)',
            textAlign: 'center',
            margin: '14px 0 0',
            lineHeight: 1.5,
          }}
        >
          We'll find CAS emails from CDSL, NSDL, KFintech and CAMS.
        </p>
        <p
          style={{
            fontSize: 11,
            color: 'var(--pc-color-text-faded)',
            textAlign: 'center',
            margin: '14px 0 0',
            lineHeight: 1.5,
          }}
        >
          Read-only access.{' '}
          <a
            href={activeProvider.permissionsUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: 'var(--pc-color-text-muted)',
              textDecoration: 'underline',
            }}
          >
            Disconnect any time
          </a>
          .
        </p>
      </div>
    );
  }

  // Loading state
  if (state === 'LOADING') {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }} aria-live="polite">
        <div style={{
          width: 48,
          height: 48,
          border: '3px solid var(--pc-color-border)',
          borderTopColor: 'var(--pc-color-primary)',
          borderRadius: '50%',
          animation: 'pc-spin 1s linear infinite',
          margin: '0 auto 16px',
        }} />
        <p style={{ fontSize: 14, color: 'var(--pc-color-text-muted)', margin: 0 }}>
          Searching your inbox for statements…
        </p>
        {connectedEmail && (
          <p style={{ fontSize: 12, color: 'var(--pc-color-text-faded)', marginTop: 8 }}>
            Connected as {connectedEmail}
          </p>
        )}
      </div>
    );
  }

  // Error state
  if (state === 'ERROR') {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <span
          style={{
            display: 'inline-flex',
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: 'var(--pc-color-error-bg)',
            color: 'var(--pc-color-error)',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 16,
          }}
        >
          <IconAlert size={28} />
        </span>
        <h3 style={{ fontSize: 18, fontWeight: 600, color: 'var(--pc-color-text)', margin: '0 0 8px' }}>
          Something went wrong
        </h3>
        <p style={{ fontSize: 14, color: 'var(--pc-color-text-muted)', margin: '0 0 20px', lineHeight: 1.5 }}>
          {error}
        </p>
        <Button
          variant="primary"
          fullWidth
          onClick={() => {
            setError(null);
            setState('CONNECT');
          }}
        >
          Try again
        </Button>
      </div>
    );
  }

  // Password entry screen
  if (state === 'PASSWORD' && selectedFile) {
    return (
      <div>
        <h3 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px', color: 'var(--pc-color-text)' }}>
          Enter your PDF password
        </h3>
        
        {/* Selected file preview */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          padding: 14,
          border: '1px solid var(--pc-color-border)',
          borderRadius: 'var(--pc-radius-md)',
          backgroundColor: 'var(--pc-color-surface-subtle)',
          marginBottom: 20,
        }}>
          <div style={{ 
            width: 40, 
            height: 40, 
            backgroundColor: 'var(--pc-color-primary-soft)', 
            borderRadius: 'var(--pc-radius-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: 12,
            color: 'var(--pc-color-primary)',
          }}>
            <IconFile size={20} />
          </div>
          <div style={{ flex: 1, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
              <span style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: 'var(--pc-radius-sm)',
                backgroundColor: 'var(--pc-color-surface-strong)',
                color: 'var(--pc-color-text)',
              }}>
                {getCasTypeLabel(selectedFile.cas_type)}
              </span>
              <span style={{ fontSize: 11, color: 'var(--pc-color-text-muted)' }}>
                {formatDate(selectedFile.message_date)}
              </span>
            </div>
            <div style={{ 
              fontWeight: 500, 
              whiteSpace: 'nowrap', 
              overflow: 'hidden', 
              textOverflow: 'ellipsis',
              fontSize: 13,
              color: 'var(--pc-color-text)',
            }}>
              {selectedFile.original_filename}
            </div>
          </div>
          <button
            onClick={() => setState('FILES')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--pc-color-text-muted)',
              cursor: 'pointer',
              fontSize: 12,
              textDecoration: 'underline',
              flexShrink: 0,
            }}
          >
            Change
          </button>
        </div>

        {/* Password input */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ 
            display: 'block', 
            marginBottom: 6, 
            fontWeight: 500, 
            fontSize: 13,
            color: 'var(--pc-color-text)',
          }}>
            PDF Password
          </label>
          <PasswordInput
            data-pc-autofocus
            autoFocus
            placeholder="Leave blank if your PDF isn't password protected"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
          />
          <div style={{ 
            fontSize: 11, 
            color: 'var(--pc-color-text-faded)', 
            marginTop: 6,
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
          }}>
            <span>Common passwords:</span>
            <span>PAN in CAPS</span>
            <span>DOB (DDMMYYYY)</span>
            <span>Leave blank if not protected</span>
          </div>
        </div>

        <Button variant="primary" fullWidth onClick={handlePasswordSubmit}>
          {password ? 'Import Statement' : 'Import without password'}
        </Button>
      </div>
    );
  }

  // Files list
  return (
    <div>
      {/* Header with connected email */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        padding: '10px 14px',
        backgroundColor: 'var(--pc-color-success-bg)',
        borderRadius: 'var(--pc-radius-md)',
        border: '1px solid var(--pc-color-success-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ color: 'var(--pc-color-success)' }}>
            <IconCheckCircle size={16} />
          </div>
          <span style={{ fontSize: 13, color: 'var(--pc-color-success)' }}>
            Connected: <strong>{connectedEmail}</strong>
          </span>
        </div>
        <button
          onClick={handleDisconnect}
          style={{
            fontSize: 12,
            color: 'var(--pc-color-error)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Disconnect
        </button>
      </div>

      {/* Files count */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: 'var(--pc-color-text)', margin: 0 }}>
          {files.length} statement{files.length !== 1 ? 's' : ''} found
        </h3>
        <p style={{ fontSize: 13, color: 'var(--pc-color-text-muted)', margin: '4px 0 0' }}>
          Select one to import
        </p>
      </div>

      {/* Files list */}
      {files.length === 0 ? (
        // Empty inbox is a common dead end. Don't just say "nothing here"
        // — guide the user to the most likely next steps. Two reasons
        // they'd see this state: (a) they've never requested a CAS, in
        // which case the mailback flow is the right move; (b) the CAS
        // is in a different inbox, in which case forwarding from there
        // works. Both nudges only appear when the embedder has the
        // corresponding feature enabled.
        <div style={{
          padding: '24px 20px',
          backgroundColor: 'var(--pc-color-surface-subtle)',
          borderRadius: 'var(--pc-radius-md)',
          border: '1px dashed var(--pc-color-border-strong)',
        }}>
          <p
            style={{
              fontSize: 14,
              color: 'var(--pc-color-text)',
              margin: 0,
              fontWeight: 500,
              textAlign: 'center',
            }}
          >
            No statements found in this inbox
          </p>
          <p
            style={{
              fontSize: 12,
              color: 'var(--pc-color-text-muted)',
              margin: '4px 0 16px',
              textAlign: 'center',
              lineHeight: 1.5,
            }}
          >
            We searched for emails from CAMS, CDSL, and NSDL.
          </p>

          {(onJumpToRequestCas || onJumpToInbound) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 12,
              }}
            >
              {onJumpToRequestCas && (
                <Button
                  variant="primary"
                  fullWidth
                  onClick={onJumpToRequestCas}
                >
                  Request a fresh mutual fund CAS
                </Button>
              )}
              {onJumpToInbound && (
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={onJumpToInbound}
                >
                  Forward from a different inbox
                </Button>
              )}
            </div>
          )}

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={() => inboxToken && loadFiles(inboxToken)}
              style={{
                fontSize: 12,
                color: 'var(--pc-color-text-muted)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                fontFamily: 'inherit',
              }}
            >
              Refresh inbox
            </button>
          </div>
        </div>
      ) : (
        <div style={{
          maxHeight: 280,
          overflowY: 'auto',
          border: '1px solid var(--pc-color-border)',
          borderRadius: 'var(--pc-radius-md)',
        }}>
          {files.map((file, idx) => {
            const isSelected = selectedFile?.message_id === file.message_id;
            
            return (
              <div
                key={file.message_id}
                onClick={() => handleFileClick(file)}
                style={{
                  padding: '12px 14px',
                  cursor: 'pointer',
                  backgroundColor: isSelected ? 'var(--pc-color-primary-soft)' : 'var(--pc-color-surface)',
                  borderBottom: idx < files.length - 1 ? '1px solid var(--pc-color-border)' : 'none',
                  transition: 'background-color 0.15s',
                }}
                onMouseOver={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--pc-color-surface-subtle)';
                }}
                onMouseOut={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--pc-color-surface)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                  {/* Radio indicator */}
                  <div style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `2px solid ${isSelected ? 'var(--pc-color-primary)' : 'var(--pc-color-border-strong)'}`,
                    backgroundColor: isSelected ? 'var(--pc-color-primary)' : 'var(--pc-color-surface)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: 2,
                  }}>
                    {isSelected && (
                      <div style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: 'var(--pc-color-surface)',
                      }} />
                    )}
                  </div>
                  
                  {/* File info - clean display */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: 'var(--pc-color-text)',
                      }}>
                        {getCasTypeLabel(file.cas_type)} Statement
                      </span>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 500,
                        padding: '2px 8px',
                        borderRadius: 'var(--pc-radius-sm)',
                        backgroundColor: 'var(--pc-color-surface-strong)',
                        color: 'var(--pc-color-text-muted)',
                      }}>
                        {file.cas_type === 'cams' || file.cas_type === 'kfintech' ? 'Mutual Funds' : 'Demat'}
                      </span>
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--pc-color-text-muted)' }}>
                      Received {formatDate(file.message_date)} · {formatFileSize(file.size)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Import button */}
      {files.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Button
            variant="primary"
            fullWidth
            onClick={handleImport}
            disabled={!selectedFile}
          >
            {selectedFile
              ? `Import ${getCasTypeLabel(selectedFile.cas_type)} statement`
              : 'Select a statement above'}
          </Button>
        </div>
      )}
    </div>
  );
};
